/**
 * @file Required-field checks driven by `structure.json` (`image`, `form`, `list` sections).
 * @module formValidation
 */

/**
 * @typedef {{ type: 'form', sectionId: string, fieldId: string }}
 *   | { type: 'listCell', sectionId: string, rowIndex: number, fieldId: string }
 *   | { type: 'listEmpty', sectionId: string }
 *   | { type: 'image', sectionId: string }
 * } CvValidationIssue
 */

/**
 * @param {unknown} v
 * @returns {boolean}
 */
function isNonEmptyString(v) {
	return typeof v === "string" && v.trim().length > 0;
}

/**
 * @param {Record<string, unknown>} data Form `readData()` result.
 * @param {Record<string, unknown>} structure Template root (`sections[]`).
 * @returns {CvValidationIssue[]}
 */
export function getCvRequiredValidationIssues(data, structure) {
	/** @type {CvValidationIssue[]} */
	const issues = [];
	const sections = Array.isArray(structure.sections)
		? structure.sections
		: [];

	for (const section of sections) {
		if (section.type === "image" && section.required === true) {
			const key = String(section.fieldKey ?? "");
			const sid = String(section.id ?? "");
			if (!key) continue;
			const v = data[key];
			const ok =
				typeof v === "string" &&
				v.trim().length > 0 &&
				v.startsWith("data:");
			if (!ok) {
				issues.push({ type: "image", sectionId: sid });
			}
		} else if (section.type === "form" && Array.isArray(section.fields)) {
			const sectionId = String(section.id ?? "");
			for (const field of section.fields) {
				if (field.required !== true) continue;
				const fieldId = String(field.id ?? "");
				if (field.inputType === "checkboxes") {
					const arr = data[fieldId];
					if (!Array.isArray(arr) || arr.length === 0) {
						issues.push({ type: "form", sectionId, fieldId });
					}
				} else {
					const v = data[fieldId];
					if (!isNonEmptyString(String(v ?? ""))) {
						issues.push({ type: "form", sectionId, fieldId });
					}
				}
			}
		} else if (
			section.type === "list" &&
			Array.isArray(section.itemFields)
		) {
			const sid = String(section.id ?? "");
			const itemFields = section.itemFields;
			const requiredItems = itemFields.filter((f) => f.required === true);
			if (requiredItems.length === 0) continue;

			const rows = Array.isArray(data[sid]) ? data[sid] : [];
			const allItemFieldsRequired =
				itemFields.length > 0 &&
				itemFields.every((f) => f.required === true);

			if (rows.length === 0) {
				if (allItemFieldsRequired) {
					issues.push({ type: "listEmpty", sectionId: sid });
				}
				continue;
			}

			for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
				const row = rows[rowIndex];
				const r =
					row && typeof row === "object" && !Array.isArray(row) ? row : {};
				for (const itemField of requiredItems) {
					const fid = String(itemField.id ?? "");
					const val = r[fid];
					if (!isNonEmptyString(String(val ?? ""))) {
						issues.push({
							type: "listCell",
							sectionId: sid,
							rowIndex,
							fieldId: fid,
						});
					}
				}
			}
		}
	}

	return issues;
}

/**
 * Quita marcas de error del contenedor del formulario.
 * @param {HTMLElement} container `#cvFormFieldsMount` o equivalente.
 */
export function clearValidationErrors(container) {
	container
		.querySelectorAll(".form-field.has-error, fieldset.has-error")
		.forEach((el) => {
			el.classList.remove("has-error");
		});
}

/**
 * Marca campos con `has-error` según incidencias.
 * @param {HTMLElement} container
 * @param {CvValidationIssue[]} issues
 */
export function applyValidationErrors(container, issues) {
	for (const issue of issues) {
		if (issue.type === "image") {
			const fs = container.querySelector(
				`fieldset[data-section-id="${cssEscape(issue.sectionId)}"]`
			);
			fs?.classList.add("has-error");
			continue;
		}
		if (issue.type === "listEmpty") {
			const fs = container.querySelector(
				`fieldset[data-section-id="${cssEscape(issue.sectionId)}"]`
			);
			fs?.classList.add("has-error");
			continue;
		}
		if (issue.type === "listCell") {
			const fs = container.querySelector(
				`fieldset[data-section-id="${cssEscape(issue.sectionId)}"]`
			);
			const rowEls = fs?.querySelectorAll(".list-row");
			const row = rowEls?.[issue.rowIndex];
			const input = row?.querySelector(
				`[data-field-id="${cssEscape(issue.fieldId)}"]`
			);
			input?.closest(".form-field")?.classList.add("has-error");
			continue;
		}
		if (issue.type === "form") {
			const fs = container.querySelector(
				`fieldset[data-section-id="${cssEscape(issue.sectionId)}"]`
			);
			const cbWrap = fs?.querySelector(
				`[data-checkbox-for="${cssEscape(issue.fieldId)}"]`
			);
			if (cbWrap) {
				cbWrap.classList.add("has-error");
				continue;
			}
			const el = fs?.querySelector(
				`[data-field-id="${cssEscape(issue.fieldId)}"]`
			);
			el?.closest(".form-field")?.classList.add("has-error");
		}
	}
}

/**
 * @param {string} s
 * @returns {string}
 */
function cssEscape(s) {
	if (typeof CSS !== "undefined" && CSS.escape) {
		return CSS.escape(s);
	}
	return s.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}
